import type { IntakeFormData, Mismatch } from "./form";

export interface IntakeFormResponse {
  id: string;
  proposalId: string;
  /** Version number — V1 is the frozen baseline; V2+ are editable copies. */
  version: number;
  status: "draft" | "submitted";
  createdAt: string;
  updatedAt: string;
  data: IntakeFormData;
  /** Which fields were pre-populated from the proposal/baseline. */
  prefilled: Record<string, boolean>;
  /**
   * Baseline values (V1 snapshot) keyed by field name. Used as the "original"
   * reference for mismatch detection — compared against `data` to surface
   * "Differs from proposal" hints both at load time and as the user types.
   */
  baseline: Record<string, unknown>;
  /** Fields where the user changed the value from the original. */
  mismatches: Record<string, Mismatch>;
  /**
   * Server-sourced metadata the form needs but isn't part of the user's input —
   * dropdown options pulled from Salesforce, etc. Populated server-side so
   * the UI doesn't carry org-specific picklist values in code.
   */
  meta: IntakeFormMeta;
  /** File IDs that should be deleted from SF (e.g. census file cleared by Claims UW). */
  staleFileIds: string[];
  /** True when employee data was modified during load-time cleanup (plan/dental/vision clearing). */
  employeesModified: boolean;
}

export interface SicCodeEntry {
  code: string;
  industryName: string;
}

export interface IntakeFormMeta {
  /** Active labels from Employer_Intake_Form__c.Organization_Type__c — drives the Org Type dropdown. */
  organizationTypeOptions: string[];
  /** Active labels from Employer_Intake_Form__c.Prior_Medical_Carrier__c — drives the Prior Medical Carrier dropdown. */
  medicalCarrierOptions: string[];
  /** Active labels from Employer_Intake_Form__c.Excluded_Classes__c — drives the excluded classes multi-select. */
  excludedClassesOptions: string[];
  /** Active labels from Employer_Intake_Form__c.Waiting_Period__c — drives the Waiting Period dropdown. */
  waitingPeriodOptions: string[];
  /** All SIC codes from vlocity_ins__SICCode__c — loaded once for local lookup. */
  sicCodes: SicCodeEntry[];
  /** Underwriting method from the Account — drives Employee Listing section on Step 7. */
  uwMethod: string | null;
  /** True when the form's Opportunity is a Renewal — Documents & Census become optional (collected at original issue). */
  isRenewal: boolean;
  /** Number of active medical plans (1-4). Drives Medical Plan selector visibility. */
  availablePlans: number;
  /** Pre-built IHQ enrollment link (null if no Contract exists yet). */
  ihqLink: string | null;
}

export interface SessionResponse {
  token: string;
  email: string;
  proposalId: string;
}

export interface LockResponse {
  acquired: boolean;
  lockedBy?: string;
  lockedAt?: string;
}

export interface FileUploadResponse {
  fileId: string;
  name: string;
  size: number;
}

export interface AuthorizationResult {
  isAuthorized: boolean;
  role: string;
  groups: Array<{ accountId: string; accountName: string; oppId?: string }>;
  oppId?: string;
}

export class ApiError extends Error {
  status: number;
  /** Parsed response body when the server returned JSON. Useful when an "allowed" non-2xx still carries data the caller wants. */
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}
